import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { z } from "zod";
import { useDebounce } from "@/hooks/use-debounce";
import {
  step1Schema,
  step2Schema,
  step3Schema,
  type MarketingProspect,
  type ValidatedDiscount,
} from "@/lib/club-register-schemas";
import {
  Building2,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Loader2,
  Mail,
  Lock,
  User,
  MapPin,
  FileText,
  Check,
  X,
  Ticket,
  Shield,
  Search,
} from "lucide-react";
import { TennisCourtIcon } from "@/components/TennisCourtIcon";
import {
  trackClubRegistrationView,
  trackClubRegistrationStep,
  trackClubRegistrationComplete,
  trackSubscriptionStart,
  trackBeginCheckout,
  trackViewItem,
} from "@/lib/analytics";
import { PRICING_TIERS, formatPrice, type PricingTier, type BillingInterval, getPriceForInterval } from "@/lib/pricing";
import { isTrialActive, FREE_TRIAL_LABEL_NL } from "@/lib/trial";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";

const ClubRegister = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const trialActive = isTrialActive();

  // Track page view on mount
  useEffect(() => {
    trackClubRegistrationView();
  }, []);

  // Availability states
  const [clubNameAvailable, setClubNameAvailable] = useState<boolean | null>(null);
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);
  const [checkingClubName, setCheckingClubName] = useState(false);

  // Discount code states
  const [discountCode, setDiscountCode] = useState("");
  const [validatedDiscount, setValidatedDiscount] = useState<ValidatedDiscount | null>(null);
  const [checkingDiscount, setCheckingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // Club search from marketing prospects
  const [clubSearchQuery, setClubSearchQuery] = useState("");
  const [clubSearchResults, setClubSearchResults] = useState<MarketingProspect[]>([]);
  const [showClubDropdown, setShowClubDropdown] = useState(false);
  const [manualClubEntry, setManualClubEntry] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState<MarketingProspect | null>(null);
  const [loadingProspects, setLoadingProspects] = useState(false);
  const debouncedClubSearch = useDebounce(clubSearchQuery, 300);

  const [formData, setFormData] = useState({
    // Step 1: Club basis
    clubName: "",
    city: "",
    streetAddress: "",
    postalCode: "",
    pricingTier: "medium" as PricingTier,
    billingInterval: "yearly" as BillingInterval,
    // Step 2: Contactpersoon
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    // Step 3: Administratie
    invoiceEmail: "",
    kvkNumber: "",
    vatNumber: "",
    website: "",
    acceptTerms: false,
  });

  const debouncedClubName = useDebounce(formData.clubName, 500);
  const debouncedEmail = useDebounce(formData.email, 500);

  // Search marketing prospects for club lookup
  useEffect(() => {
    const searchProspects = async () => {
      if (debouncedClubSearch.length < 2) {
        setClubSearchResults([]);
        return;
      }
      setLoadingProspects(true);
      try {
        const { data, error } = await supabase
          .from("marketing_prospects_public" as any)
          .select("id, club_name, city, address, postal_code, sport_type")
          .ilike("club_name", `%${debouncedClubSearch}%`)
          .limit(10);
        if (error) throw error;
        setClubSearchResults((data as any as MarketingProspect[]) || []);
      } catch (error) {
        console.error("Error searching prospects:", error);
        setClubSearchResults([]);
      } finally {
        setLoadingProspects(false);
      }
    };
    searchProspects();
  }, [debouncedClubSearch]);

  const selectProspectClub = (prospect: MarketingProspect) => {
    setSelectedProspect(prospect);
    setFormData(prev => ({
      ...prev,
      clubName: prospect.club_name,
      city: prospect.city || "",
      streetAddress: prospect.address || "",
      postalCode: prospect.postal_code || "",
    }));
    setClubSearchQuery(prospect.club_name);
    setShowClubDropdown(false);
    setManualClubEntry(false);
  };


  useEffect(() => {
    const checkClubName = async () => {
      if (debouncedClubName.length < 2) {
        setClubNameAvailable(null);
        return;
      }

      setCheckingClubName(true);
      try {
        const { data, error } = await supabase.from("clubs_public").select("id").ilike("name", debouncedClubName).limit(1);

        if (error) throw error;
        setClubNameAvailable(data.length === 0);
      } catch (error) {
        console.error("Error checking club name:", error);
        setClubNameAvailable(null);
      } finally {
        setCheckingClubName(false);
      }
    };

    checkClubName();
  }, [debouncedClubName]);

  // Check email availability - admin cannot also be a player
  useEffect(() => {
    const checkEmail = async () => {
      // First validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(debouncedEmail)) {
        setEmailAvailable(null);
        return;
      }

      setCheckingEmail(true);
      try {
        // Check in clubs admin_email
        const { data: clubData, error: clubError } = await supabase
          .from("clubs")
          .select("id")
          .eq("admin_email", debouncedEmail)
          .limit(1);

        if (clubError) throw clubError;

        // If found in clubs as admin, email is taken
        if (clubData && clubData.length > 0) {
          setEmailAvailable(false);
          return;
        }

        // Check in profiles table - admin cannot also be a player
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("id, user_id")
          .limit(1);
        
        // We need to check auth.users - use the edge function validation instead
        // For now, we'll rely on the create-subscription edge function to do the full check
        // But we can check pending_registrations for in-flight registrations
        const { data: pendingData, error: pendingError } = await supabase
          .from("pending_registrations")
          .select("id")
          .eq("email", debouncedEmail)
          .limit(1);

        if (!pendingError && pendingData && pendingData.length > 0) {
          setEmailAvailable(false);
          return;
        }

        // Email appears available (final check happens server-side)
        setEmailAvailable(true);
      } catch (error) {
        console.error("Error checking email:", error);
        setEmailAvailable(null);
      } finally {
        setCheckingEmail(false);
      }
    };

    checkEmail();
  }, [debouncedEmail]);

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setValidatedDiscount(null);
      setDiscountError(null);
      return;
    }

    setCheckingDiscount(true);
    setDiscountError(null);

    try {
      // Server-side validation via SECURITY DEFINER RPC.
      // Prevents enumeration of all discount codes — only returns a match for the exact code.
      const { data, error } = await (supabase.rpc as any)("validate_discount_code", {
        _code: discountCode.trim(),
      });

      if (error) {
        console.error("Error fetching discount code:", error);
        setDiscountError("Fout bij valideren kortingscode");
        setValidatedDiscount(null);
        return;
      }

      const row = Array.isArray(data) ? data[0] : data;

      if (!row) {
        setDiscountError("Ongeldige of verlopen kortingscode");
        setValidatedDiscount(null);
        return;
      }

      // max_uses / validity already enforced server-side in the RPC.
      setValidatedDiscount({
        code: row.code,
        discount_type: row.discount_type as "percentage" | "fixed",
        discount_value: Number(row.discount_value),
        discount_months: row.discount_months,
      });
      setDiscountError(null);
    } catch (error) {
      console.error("Error validating discount code:", error);
      setDiscountError("Fout bij valideren kortingscode");
      setValidatedDiscount(null);
    } finally {
      setCheckingDiscount(false);
    }
  };

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));

    // Reset availability when field changes
    if (field === "clubName") {
      setClubNameAvailable(null);
    }
    if (field === "email") {
      setEmailAvailable(null);
    }
  };

  const validateStep = (stepNum: number): boolean => {
    try {
      if (stepNum === 1) {
        step1Schema.parse({
          clubName: formData.clubName,
          city: formData.city,
          streetAddress: formData.streetAddress,
          postalCode: formData.postalCode,
        });
      } else if (stepNum === 2) {
        step2Schema.parse({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
        });
      } else if (stepNum === 3) {
        step3Schema.parse({
          invoiceEmail: formData.invoiceEmail,
          kvkNumber: formData.kvkNumber,
          vatNumber: formData.vatNumber,
          website: formData.website,
        });
      }
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      const stepNames = ["club_details", "contact_person", "administration"];
      trackClubRegistrationStep(step, stepNames[step - 1]);
      setStep(step + 1);
    } else {
      toast.error("Vul alle verplichte velden correct in");
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    // Check terms acceptance
    if (!formData.acceptTerms) {
      toast.error("Je moet akkoord gaan met de Algemene Voorwaarden");
      return;
    }

    setLoading(true);
    try {
      trackClubRegistrationComplete(formData.clubName);

      // Enhanced ecommerce: begin_checkout
      // Use actual payment amount based on billing interval
      const checkoutPrice = formData.billingInterval === 'yearly'
        ? selectedTier.yearlyTotalExclBTW
        : selectedTier.monthlyPriceExclBTW;
      const discountAmount = validatedDiscount
        ? validatedDiscount.discount_type === 'percentage'
          ? checkoutPrice * (validatedDiscount.discount_value / 100)
          : validatedDiscount.discount_value
        : 0;

      trackBeginCheckout({
        clubName: formData.clubName,
        pricingTier: formData.pricingTier,
        priceExclBTW: checkoutPrice,
        coupon: validatedDiscount?.code,
        discount: discountAmount || undefined,
      });

      const invokePromise = supabase.functions.invoke("create-subscription", {
        body: {
          registrationData: {
            clubName: formData.clubName,
            city: formData.city,
            streetAddress: formData.streetAddress || null,
            postalCode: formData.postalCode || null,
            email: formData.email,
            password: formData.password,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone || null,
            invoiceEmail: formData.invoiceEmail || null,
            kvkNumber: formData.kvkNumber || null,
            vatNumber: formData.vatNumber || null,
            website: formData.website || null,
            pricingTier: formData.pricingTier,
            billingInterval: formData.billingInterval,
          },
          redirectUrl: `${window.location.origin}/auth?registration=success`,
          discountCode: validatedDiscount?.code || null,
        },
      });

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("REQUEST_TIMEOUT")), 35000);
      });

      const { data: subData, error: subError } = await Promise.race([
        invokePromise,
        timeoutPromise,
      ]);

      if (subError) throw subError;

      // Trial mode: club is created instantly, no Mollie redirect.
      if (subData?.trial) {
        setLoading(false);
        toast.success(`Je club is geregistreerd — ${FREE_TRIAL_LABEL_NL}.`);
        navigate("/auth?registration=success&trial=1");
        return;
      }

      if (subData.checkoutUrl) {
        // Show redirect indicator before navigating
        setLoading(false);
        setRedirecting(true);
        // Store checkout data for GA4 purchase event after redirect
        sessionStorage.setItem('hussel_checkout', JSON.stringify({
          clubName: formData.clubName,
          pricingTier: formData.pricingTier,
          billingInterval: formData.billingInterval,
          priceExclBTW: checkoutPrice,
          coupon: validatedDiscount?.code,
          discount: discountAmount || undefined,
        }));
        window.location.href = subData.checkoutUrl;
      } else {
        toast.success("Club aangemaakt! Controleer je e-mail om je account te activeren.");
        navigate("/auth");
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      // Realtime error log → zichtbaar in Test & Debug → Userflow checklist
      try {
        const { logRealtimeError } = await import("@/lib/error-logger");
        void logRealtimeError({
          flow: "club-register",
          message: error?.message || "Club registratie faalde",
          stack: error?.stack,
          context: { code: error?.code },
        });
      } catch {
        // ignore logging failures
      }

      let backendMessage: string | null = null;
      const response = error?.context;
      if (response && typeof response.text === "function") {
        try {
          const rawBody = await Promise.race<string>([
            response.text(),
            new Promise<string>((_, reject) => {
              setTimeout(() => reject(new Error("RESPONSE_PARSE_TIMEOUT")), 1500);
            }),
          ]);

          if (rawBody) {
            try {
              const payload = JSON.parse(rawBody);
              if (payload?.error && typeof payload.error === "string") {
                backendMessage = payload.error;
              } else if (payload?.message && typeof payload.message === "string") {
                backendMessage = payload.message;
              }
            } catch {
              backendMessage = rawBody.slice(0, 180);
            }
          }
        } catch {
          backendMessage = null;
        }
      }

      if (error.message === "REQUEST_TIMEOUT") {
        toast.error("De betaalservice reageert te traag. Probeer het opnieuw.");
      } else if (backendMessage) {
        toast.error(backendMessage);
      } else if (error.message?.includes("already registered")) {
        toast.error("Dit e-mailadres is al geregistreerd. Probeer in te loggen.");
      } else if (error.code === "23505") {
        toast.error("Deze clubnaam is al in gebruik. Kies een andere naam.");
      } else {
        toast.error(error.message || "Er ging iets mis bij de registratie");
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedTier = PRICING_TIERS.find((t) => t.id === formData.pricingTier)!;
  const intervalPricing = getPriceForInterval(formData.pricingTier, formData.billingInterval);
  // Calculate discounted price
  // Calculate discounted price
  const calculateDiscountedPrice = () => {
    // Calculate discount on the per-month equivalent price
    const basePrice = intervalPricing.perMonth;
    if (!validatedDiscount) return basePrice;

    if (validatedDiscount.discount_type === "percentage") {
      return basePrice * (1 - validatedDiscount.discount_value / 100);
    } else {
      return Math.max(0, basePrice - validatedDiscount.discount_value);
    }
  };

  // Calculate discounted total for yearly
  const calculateDiscountedTotal = () => {
    if (formData.billingInterval !== 'yearly') return null;
    const baseTotal = selectedTier.yearlyTotalExclBTW;
    if (!validatedDiscount) return baseTotal;

    if (validatedDiscount.discount_type === "percentage") {
      return baseTotal * (1 - validatedDiscount.discount_value / 100);
    } else {
      return Math.max(0, baseTotal - validatedDiscount.discount_value);
    }
  };

  const discountedPrice = calculateDiscountedPrice();
  const discountedTotal = calculateDiscountedTotal();

  const features = [
    "Onbeperkt aantal sessies",
    "Automatische & slimme indelingen",
    "Gratis mobiele app voor spelers",
    "Club branding",
    "Realtime banenindeling",
    "Banen management",
    "Meldingen & push notificaties",
    selectedTier.id === 'small' ? "Max. 150 leden" : selectedTier.id === 'medium' ? "Max. 450 leden" : "Onbeperkt aantal leden",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto max-w-6xl px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
              <img src="/hussel-app-logo-2027-192.png" alt="De Hussel App" className="h-12 w-12 object-contain" />
            </div>
            <Button variant="ghost" onClick={() => navigate("/auth")}>
              Inloggen
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto max-w-6xl px-3 py-6 md:py-12">
        <div className="grid lg:grid-cols-2 gap-5 lg:gap-12">
          {/* Left: Registration Form */}
          <div>
            <div className="mb-8">
              <Badge className="mb-4">Stap {step} van 3</Badge>
              <h1 className="text-3xl font-bold mb-2">
                {step === 1 && "Club gegevens"}
                {step === 2 && "Contactpersoon"}
                {step === 3 && "Administratie"}
              </h1>
              <p className="text-muted-foreground">
                {step === 1 && "Vertel ons over je tennisclub"}
                {step === 2 && "Gegevens van de hoofdcontactpersoon"}
                {step === 3 && "Optionele administratiegegevens voor je club"}
              </p>
            </div>

            {/* Progress indicator */}
            <div className="flex gap-2 mb-8">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? "bg-primary" : "bg-muted"}`}
                />
              ))}
            </div>

            <Card className="p-6">
              {step === 1 && (
                <div className="space-y-4">
                  {/* Pricing Tier Selection */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">Kies je pakket *</Label>
                    <RadioGroup
                      value={formData.pricingTier}
                      onValueChange={(value) => updateField("pricingTier", value)}
                      className="grid gap-3"
                    >
                      {PRICING_TIERS.map((tier) => (
                        <div key={tier.id} className="relative">
                          <RadioGroupItem value={tier.id} id={tier.id} className="peer sr-only" />
                          <Label
                            htmlFor={tier.id}
                            className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all
                              ${
                                formData.pricingTier === tier.id
                                  ? "border-primary bg-primary/5"
                                  : "border-border hover:border-primary/50"
                              }
                              ${tier.isPopular ? "ring-2 ring-primary ring-offset-2" : ""}
                            `}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                                ${formData.pricingTier === tier.id ? "border-primary bg-primary" : "border-muted-foreground"}
                              `}
                              >
                                {formData.pricingTier === tier.id && (
                                  <Check className="h-3 w-3 text-primary-foreground" />
                                )}
                              </div>
                              <div>
                                <p className="font-semibold flex items-center gap-2">
                                  {tier.name}
                                  {tier.isPopular && (
                                    <Badge variant="secondary" className="text-xs">
                                      Populair
                                    </Badge>
                                  )}
                                </p>
                                <p className="text-sm text-muted-foreground">{tier.memberRange}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg">€{formatPrice(formData.billingInterval === 'yearly' ? tier.priceExclBTW : tier.monthlyPriceExclBTW)}</p>
                              <p className="text-xs text-muted-foreground">maand</p>
                            </div>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1.5">
                      <Shield className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-accent" />
                      <span>Het aantal actieve clubleden (conform KNLTB-registratie) wordt geverifieerd. Indien nodig wordt het pakket aangepast naar de juiste grootte.</span>
                    </p>
                  </div>

                  {/* Billing Interval Toggle */}
                  <div className="space-y-3 border-t pt-4">
                    <Label className="text-base font-semibold">Abonnementstype</Label>
                    <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                      <span className={`text-sm font-medium ${formData.billingInterval === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
                        Maand-Flex
                      </span>
                      <Switch
                        checked={formData.billingInterval === 'yearly'}
                        onCheckedChange={(checked) => updateField("billingInterval", checked ? 'yearly' : 'monthly')}
                      />
                      <span className={`text-sm font-medium ${formData.billingInterval === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}>
                        Jaarlijks
                      </span>
                      {formData.billingInterval === 'yearly' && (
                        <Badge className="text-xs" style={{ backgroundColor: '#e5e600', color: '#171717', borderColor: '#e5e600' }}>
                          Bespaar {selectedTier.savingsPercent}%
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Weergegeven prijzen zijn exclusief 21% btw</p>
                    <p className="text-xs text-muted-foreground">
                      {formData.billingInterval === 'yearly'
                        ? `Jaarabonnement: €${formatPrice(selectedTier.yearlyTotalExclBTW)} jaar (€${formatPrice(selectedTier.priceExclBTW)} mnd). Bespaar €${formatPrice(selectedTier.savingsTotal)} per jaar.`
                        : `Maand-Flex: €${formatPrice(selectedTier.monthlyPriceExclBTW)} mnd, maandelijks opzegbaar.`}
                    </p>
                  </div>

                  {/* Club Search */}
                  <div className="space-y-2">
                    <Label>
                      <Building2 className="inline h-4 w-4 mr-1" />
                      Clubnaam *
                    </Label>

                    {!manualClubEntry ? (
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            value={clubSearchQuery}
                            onChange={(e) => {
                              setClubSearchQuery(e.target.value);
                              setShowClubDropdown(true);
                              if (selectedProspect) {
                                setSelectedProspect(null);
                                updateField("clubName", e.target.value);
                                updateField("city", "");
                                updateField("streetAddress", "");
                                updateField("postalCode", "");
                              }
                            }}
                            onFocus={() => setShowClubDropdown(true)}
                            placeholder="Zoek je club..."
                            className="pl-10"
                          />
                        </div>

                        {showClubDropdown && clubSearchQuery.length >= 2 && (
                          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg">
                            <ScrollArea className="max-h-[200px]">
                              {loadingProspects ? (
                                <div className="p-4 flex items-center justify-center">
                                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                </div>
                              ) : clubSearchResults.length === 0 ? (
                                <div className="p-4 text-center text-sm text-muted-foreground">
                                  Geen clubs gevonden
                                </div>
                              ) : (
                                <div className="p-1">
                                  {clubSearchResults.map((prospect) => (
                                    <button
                                      key={prospect.id}
                                      type="button"
                                      onClick={() => selectProspectClub(prospect)}
                                      className="w-full flex items-center gap-3 p-2 rounded-md text-left hover:bg-accent transition-colors"
                                    >
                                      <Building2 className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate">{prospect.club_name}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {[prospect.city, prospect.postal_code].filter(Boolean).join(" · ")}
                                        </p>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </ScrollArea>
                          </div>
                        )}

                        {selectedProspect && !showClubDropdown && (
                          <div className="flex items-center gap-2 p-2 bg-accent/30 rounded-md mt-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium">{selectedProspect.club_name}</span>
                              {selectedProspect.city && (
                                <span className="text-xs text-muted-foreground ml-2">{selectedProspect.city}</span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedProspect(null);
                                setClubSearchQuery("");
                                updateField("clubName", "");
                                updateField("city", "");
                                updateField("streetAddress", "");
                                updateField("postalCode", "");
                                setShowClubDropdown(true);
                              }}
                              className="text-xs text-muted-foreground hover:text-foreground"
                            >
                              Wijzigen
                            </button>
                          </div>
                        )}

                        <button
                          type="button"
                          data-testid="club-manual-entry"
                          onClick={() => {
                            setManualClubEntry(true);
                            setSelectedProspect(null);
                            setClubSearchQuery("");
                            setShowClubDropdown(false);
                          }}
                          className="text-xs text-primary hover:underline mt-1"
                        >
                          Club niet gevonden? Handmatig toevoegen
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <Input
                            id="clubName"
                            data-testid="register-club-name"
                            value={formData.clubName}
                            onChange={(e) => updateField("clubName", e.target.value)}
                            placeholder="Tennisvereniging Markant"
                            className={`pr-10 ${errors.clubName ? "border-destructive" : clubNameAvailable === true ? "border-green-500" : clubNameAvailable === false ? "border-destructive" : ""}`}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {checkingClubName && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                            {!checkingClubName && clubNameAvailable === true && (
                              <Check className="h-4 w-4 text-green-500" />
                            )}
                            {!checkingClubName && clubNameAvailable === false && <X className="h-4 w-4 text-destructive" />}
                          </div>
                        </div>
                        {clubNameAvailable === true && formData.clubName.length >= 2 && (
                          <p className="text-sm text-green-600 flex items-center gap-1">
                            <Check className="h-3 w-3" />
                            Beschikbaar
                          </p>
                        )}
                        {clubNameAvailable === false && (
                          <p className="text-sm text-destructive">Deze clubnaam is al in gebruik</p>
                        )}
                        {errors.clubName && <p className="text-sm text-destructive">{errors.clubName}</p>}
                        <button
                          type="button"
                          onClick={() => {
                            setManualClubEntry(false);
                            updateField("clubName", "");
                            updateField("city", "");
                            updateField("streetAddress", "");
                            updateField("postalCode", "");
                          }}
                          className="text-xs text-primary hover:underline"
                        >
                          ← Terug naar zoeken
                        </button>
                      </>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">
                      <MapPin className="inline h-4 w-4 mr-1" />
                      Plaats *
                    </Label>
                    <Input
                      id="city"
                      data-testid="register-city"
                      value={formData.city}
                      onChange={(e) => updateField("city", e.target.value)}
                      placeholder="Amsterdam"
                      className={errors.city ? "border-destructive" : ""}
                      readOnly={!!selectedProspect}
                    />
                    {errors.city && <p className="text-sm text-destructive">{errors.city}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="streetAddress">Straat en huisnummer</Label>
                    <Input
                      id="streetAddress"
                      value={formData.streetAddress}
                      onChange={(e) => updateField("streetAddress", e.target.value)}
                      placeholder="Tennislaan 1"
                      readOnly={!!selectedProspect}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postalCode">Postcode</Label>
                    <Input
                      id="postalCode"
                      value={formData.postalCode}
                      onChange={(e) => updateField("postalCode", e.target.value)}
                      placeholder="1234 AB"
                      readOnly={!!selectedProspect}
                    />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">
                        <User className="inline h-4 w-4 mr-1" />
                        Voornaam *
                      </Label>
                      <Input
                        id="firstName"
                        data-testid="register-first-name"
                        value={formData.firstName}
                        onChange={(e) => updateField("firstName", e.target.value)}
                        placeholder="Jan"
                        className={errors.firstName ? "border-destructive" : ""}
                      />
                      {errors.firstName && <p className="text-sm text-destructive">{errors.firstName}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Achternaam *</Label>
                      <Input
                        id="lastName"
                        data-testid="register-last-name"
                        value={formData.lastName}
                        onChange={(e) => updateField("lastName", e.target.value)}
                        placeholder="Jansen"
                        className={errors.lastName ? "border-destructive" : ""}
                      />
                      {errors.lastName && <p className="text-sm text-destructive">{errors.lastName}</p>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      <Mail className="inline h-4 w-4 mr-1" />
                      E-mailadres *
                    </Label>
                    <div className="relative">
                      <Input
                        id="email"
                        data-testid="register-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        placeholder="jan@tennisclub.nl"
                        className={`pr-10 ${errors.email ? "border-destructive" : emailAvailable === true ? "border-green-500" : emailAvailable === false ? "border-destructive" : ""}`}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {checkingEmail && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                        {!checkingEmail && emailAvailable === true && <Check className="h-4 w-4 text-green-500" />}
                        {!checkingEmail && emailAvailable === false && <X className="h-4 w-4 text-destructive" />}
                      </div>
                    </div>
                    {emailAvailable === true && (
                      <p className="text-sm text-green-600 flex items-center gap-1">
                        <Check className="h-3 w-3" />
                        Beschikbaar
                      </p>
                    )}
                    {emailAvailable === false && (
                      <p className="text-sm text-destructive">Dit e-mailadres is al in gebruik</p>
                    )}
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefoonnummer</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                      placeholder="06-12345678"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">
                      <Lock className="inline h-4 w-4 mr-1" />
                      Wachtwoord *
                    </Label>
                    <Input
                      id="password"
                      data-testid="register-password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => updateField("password", e.target.value)}
                      placeholder="Minimaal 8 tekens"
                      className={errors.password ? "border-destructive" : ""}
                    />
                    {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  {trialActive && (
                    <div className="rounded-lg border-2 border-accent/40 bg-accent/10 p-4">
                      <p className="font-semibold text-foreground flex items-center gap-2">
                        🎉 Nu {FREE_TRIAL_LABEL_NL}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Geen betaalmethode nodig. Je club is direct actief na registratie. Daarna kun je kiezen om door te gaan met een abonnement op het gekozen pakket.
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        ℹ️ De Hussel App is op dit moment een beta-versie. Er kunnen nog kleine fouten in zitten — jouw feedback helpt ons direct met doorontwikkelen.
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="invoiceEmail">
                      <FileText className="inline h-4 w-4 mr-1" />
                      E-mail facturen
                    </Label>
                    <Input
                      id="invoiceEmail"
                      type="email"
                      value={formData.invoiceEmail}
                      onChange={(e) => updateField("invoiceEmail", e.target.value)}
                      placeholder={formData.email || "facturen@tennisclub.nl"}
                      className={errors.invoiceEmail ? "border-destructive" : ""}
                    />
                    <p className="text-xs text-muted-foreground">
                      Laat leeg om het e-mailadres van de contactpersoon te gebruiken
                    </p>
                    {errors.invoiceEmail && <p className="text-sm text-destructive">{errors.invoiceEmail}</p>}
                    {errors.invoiceEmail && <p className="text-sm text-destructive">{errors.invoiceEmail}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="kvkNumber">KvK-nummer</Label>
                      <Input
                        id="kvkNumber"
                        value={formData.kvkNumber}
                        onChange={(e) => updateField("kvkNumber", e.target.value)}
                        placeholder="12345678"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vatNumber">BTW-nummer</Label>
                      <Input
                        id="vatNumber"
                        value={formData.vatNumber}
                        onChange={(e) => updateField("vatNumber", e.target.value)}
                        placeholder="NL123456789B01"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={(e) => updateField("website", e.target.value)}
                      placeholder="www.tennisclub.nl"
                    />
                  </div>

                  {/* Discount Code — hidden during free-trial period */}
                  {!trialActive && (
                  <div className="space-y-2 border-t pt-4 mt-4">
                    <Label htmlFor="discountCode">
                      <Ticket className="inline h-4 w-4 mr-1" />
                      Kortingscode (optioneel)
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="discountCode"
                        value={discountCode}
                        onChange={(e) => {
                          setDiscountCode(e.target.value.toUpperCase());
                          setValidatedDiscount(null);
                          setDiscountError(null);
                        }}
                        placeholder="kortingscode"
                        className={`flex-1 ${validatedDiscount ? "border-green-500" : discountError ? "border-destructive" : ""}`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={validateDiscountCode}
                        disabled={checkingDiscount || !discountCode.trim()}
                      >
                        {checkingDiscount ? <Loader2 className="h-4 w-4 animate-spin" /> : "Toepassen"}
                      </Button>
                    </div>
                    {validatedDiscount && (
                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <Check className="h-4 w-4" />
                        <span>
                          Korting:{" "}
                          {validatedDiscount.discount_type === "percentage"
                            ? `${validatedDiscount.discount_value}%`
                            : `€${formatPrice(validatedDiscount.discount_value)}`}
                        {validatedDiscount.discount_months && (
                            <span className="text-muted-foreground ml-1">
                              (voor {validatedDiscount.discount_months}{" "}
                              {validatedDiscount.discount_months === 1 ? "periode" : "periodes"})
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    {discountError && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <X className="h-3 w-3" />
                        {discountError}
                      </p>
                    )}
                  </div>
                  )}

                  <div className="flex items-start gap-3 p-3 bg-accent/20 rounded-lg mt-4">
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      data-testid="register-accept-terms"
                      checked={formData.acceptTerms}
                      onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                      className="mt-1 rounded border-input"
                    />
                    <Label htmlFor="acceptTerms" className="text-sm leading-relaxed cursor-pointer">
                      Ik ga namens de club akkoord met de{" "}
                      <a
                        href="/algemene-voorwaarden"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Algemene Voorwaarden
                      </a>{" "}
                      en het{" "}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Privacybeleid
                      </a>
                    </Label>
                  </div>
                </div>
              )}

              <div className="flex gap-4 mt-6">
                {step > 1 && (
                  <Button variant="outline" onClick={prevStep} className="gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    Terug
                  </Button>
                )}
                {step < 3 ? (
                  <Button
                    data-testid="register-next-step"
                    onClick={nextStep}
                    className="flex-1 gap-2"
                  >
                    Volgende
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    data-testid="register-submit"
                    onClick={handleSubmit}
                    disabled={loading || redirecting}
                    className="flex-1 gap-2"
                  >
                    {redirecting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Je wordt doorgestuurd naar de betaalpagina...
                      </>
                    ) : loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Bezig...
                      </>
                    ) : (
                      <>
                        {trialActive ? "Registreer gratis t/m okt 2026" : "Registreren & Betalen"}
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </Card>

            <p className="text-center text-sm text-muted-foreground mt-4">
              Al een account?{" "}
              <button onClick={() => navigate("/auth")} className="text-primary hover:underline">
                Log hier in
              </button>
            </p>
          </div>

          {/* Right: Pricing Summary Card */}
          <div className="lg:sticky lg:top-8 lg:self-start">
            <Card className={`p-6 border-2 ${selectedTier.isPopular ? "border-primary" : "border-border"}`}>
              <div className="flex items-center justify-between mb-4">
                <Badge className={selectedTier.isPopular ? "bg-primary" : "bg-muted"}>{selectedTier.name}</Badge>
                <span className="text-sm text-muted-foreground">{selectedTier.memberRange}</span>
              </div>

              {validatedDiscount ? (
                <div className="mb-4">
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-lg text-muted-foreground line-through">
                      €{formatPrice(intervalPricing.perMonth)}
                    </span>
                    <span className="text-sm text-muted-foreground"> maand</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-lg text-green-600">€</span>
                    <span className="text-5xl font-bold text-green-600">{formatPrice(discountedPrice)}</span>
                    <span className="text-muted-foreground"> maand</span>
                  </div>
                  <p className="text-sm text-green-600 mb-2">
                    {validatedDiscount.discount_months
                      ? `Korting voor ${validatedDiscount.discount_months} ${validatedDiscount.discount_months === 1 ? "periode" : "periodes"}`
                      : "Korting toegepast"}
                  </p>
                  <p className="text-sm text-muted-foreground">excl. 21% btw</p>
                  {formData.billingInterval === 'yearly' && discountedTotal !== null && (
                    <p className="text-xs text-green-600 font-medium mt-1">
                      Jaarbedrag: €{formatPrice(discountedTotal)} excl. BTW
                    </p>
                  )}
                  <Badge className="mt-2" variant="outline">
                    {formData.billingInterval === 'yearly' ? 'Jaarabonnement' : 'Maand-Flex'}
                  </Badge>
                </div>
              ) : (
                <>
                  {formData.billingInterval === 'yearly' && (
                    <p className="text-sm text-muted-foreground line-through mb-1">
                      €{formatPrice(selectedTier.monthlyPriceExclBTW)} maand
                    </p>
                  )}
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-lg text-muted-foreground">€</span>
                    <span className="text-5xl font-bold">{formatPrice(intervalPricing.perMonth)}</span>
                    <span className="text-muted-foreground"> maand</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">excl. 21% btw</p>
                  {formData.billingInterval === 'yearly' && (
                    <p className="text-xs text-green-600 font-medium mb-4">
                      €{formatPrice(selectedTier.yearlyTotalExclBTW)} jaar · Bespaar €{formatPrice(selectedTier.savingsTotal)}
                    </p>
                  )}
                  <Badge className="mb-4" variant="outline">
                    {formData.billingInterval === 'yearly' ? 'Jaarabonnement' : 'Maand-Flex'}
                  </Badge>
                </>
              )}

              <ul className="space-y-3 mb-6">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="border-t pt-4 space-y-3 text-sm text-muted-foreground">
                <p>✓ {formData.billingInterval === 'yearly' ? 'Direct actief na betaling van jaarbedrag' : 'Eerste maand direct actief na betaling'}</p>
                <p>✓ {formData.billingInterval === 'yearly' ? 'Voordeligste optie — bespaar tot 44%' : 'Maandelijks opzegbaar'}</p>
                <p>✓ Betaling via iDEAL, creditcard of bankoverschrijving</p>
                <div className="flex items-center gap-3 pt-2">
                  <img src="https://www.mollie.com/external/icons/payment-methods/ideal.svg" alt="iDEAL" className="h-8" />
                  <img src="https://www.mollie.com/external/icons/payment-methods/bancontact.svg" alt="Bancontact" className="h-8" />
                  <img src="https://www.mollie.com/external/icons/payment-methods/directdebit.svg" alt="SEPA Incasso" className="h-8" />
                  <img src="https://www.mollie.com/external/icons/payment-methods/creditcard.svg" alt="Creditcard" className="h-8" />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubRegister;
